import { useMemo } from "react";

import type { GenericGraphProps } from "@similarweb/pro-logic-components";
import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import ChartStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/chart/chartService";
import type {
    ChartObject,
    ChartTrendSeries,
    CompareChartGraphDataItem,
} from "app/pages/sales-intelligence/modules/ai-agent/services/chart/types";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";

import { AIAgentEmailGraphContent } from "./AIAgentEmailGraphContent";
import { StyledHoverWrapper, StyledBlurOverlay } from "./styles";

const SUPPORTED_CHART_TYPES = new Set<ChartObject["chart"]["type"]>(["bar", "line", "column"]);

export const AIAgentEmailGraphContainer = ({
    chart,
    selectedCompany,
    isSimplifiedView = false,
    onApplyVisual,
}: {
    chart?: Record<string, unknown> | null;
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    isSimplifiedView?: boolean;
    onApplyVisual?: () => void;
}) => {
    const isChartObject = ChartStreamService.isChartObject(chart);
    const isCompareChart = useMemo(
        () => !!chart && isChartObject && ChartStreamService.isCompareChart(chart),
        [chart, isChartObject],
    );

    const transformedChartData: ChartTrendSeries[] | CompareChartGraphDataItem[] | GenericGraphProps = useMemo(
        () =>
            chart && isChartObject
                ? isCompareChart
                    ? ChartStreamService.transformDataToCompareChart(chart, selectedCompany)
                    : ChartStreamService.transformDataToChart(chart, selectedCompany)
                : [],
        [chart, selectedCompany],
    );

    if (!chart || !isChartObject) {
        return null;
    }

    const type = chart.chart.type;
    const hasData = transformedChartData.length > 0;
    const isSupportedType = SUPPORTED_CHART_TYPES.has(type);

    if (!isSupportedType || !hasData) {
        return null;
    }

    if (isSimplifiedView) {
        return (
            <StyledHoverWrapper>
                <AIAgentEmailGraphContent
                    isCompareChart={isCompareChart}
                    chart={chart}
                    transformedChartData={transformedChartData}
                    isSimplifiedView
                />
                <StyledBlurOverlay>
                    <Button onClick={onApplyVisual}>{translate("si.common.button.apply")}</Button>
                </StyledBlurOverlay>
            </StyledHoverWrapper>
        );
    }

    return (
        <AIAgentEmailGraphContent
            chart={chart}
            isCompareChart={isCompareChart}
            transformedChartData={transformedChartData}
            selectedCompany={selectedCompany}
        />
    );
};
